import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchReviewsThumbnailPhotos } from "slices/reviews-thumbnail-carousel-slice";
import ThumbnailImages from "./thumbnail-images";
import RightArrow from "images/right-pagination-arrow.svg";

export const THUMBNAIL_PAGE_SIZE = 20;
export const getOffsetFromThumbnailPage = page => {
  return (page - 1) * THUMBNAIL_PAGE_SIZE;
};

export default function ReviewThumbNailCarousel(props) {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const momentsWithCount = useSelector(state => state.reviewsThumbnailPhotos);
  const isMobileViewport = useSelector(state => state.browser?.isMobileViewport);
  const reviewsThumbnailPhotos = momentsWithCount?.moments;
  const [page, setPage] = useState(1);

  const hasReviewCoverPhotoForStyle =
    Array.isArray(reviewsThumbnailPhotos) &&
    reviewsThumbnailPhotos.length > 0 &&
    reviewsThumbnailPhotos[0]?.styleName === props.productId; // this extra clause is to support product drawers

  // this scroll function is used to load images asynchronously - like an infinite scroll
  // concept of this scroll being if 50% of the recent page loaded is viewed then load a new image behind the scene
  // for which the value is calculated in thresholdPercentageImageViewed

  // TODO: figure out when to stop fetching pages - depending on number of photo reviews
  const handleScroll = () => {
    if (!(containerRef.current && Number.isInteger(page) && page > 0)) return;

    const scrollLeft = containerRef.current.scrollLeft;
    const clientWidth = containerRef.current.clientWidth;
    const scrollWidth = containerRef.current.scrollWidth;

    const percentageImageViewed = ((scrollLeft + clientWidth) / scrollWidth) * 100;
    const thresholdPercentageImageViewed = ((page - 1) / page) * 100 + (1 / page) * 100 * 0.25;

    const isNewPageAvailable = Math.ceil((momentsWithCount?.totalCount ?? 0) / 20) > page;

    if (percentageImageViewed > thresholdPercentageImageViewed && isNewPageAvailable) {
      setPage(page + 1);
    }
  };

  const scrollOnClick = direction => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: direction === "right" ? 300 : -300,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    // to load data if SSR data is not available (like in product drawer)
    if (!hasReviewCoverPhotoForStyle) {
      dispatch(
        fetchReviewsThumbnailPhotos({
          productId: props.productId,
          offset: getOffsetFromThumbnailPage(1),
          limit: THUMBNAIL_PAGE_SIZE,
          orderBy: "featured",
        })
      );
    }
  }, []);

  useEffect(() => {
    // Checking in case the modal has side loaded more images
    const noOfReviewPagesInState = reviewsThumbnailPhotos?.length / THUMBNAIL_PAGE_SIZE;

    // the first page is always available either by SSR or by the above useEffect
    if (page > 1 && page > noOfReviewPagesInState) {
      dispatch(
        fetchReviewsThumbnailPhotos({
          productId: props.productId,
          offset: getOffsetFromThumbnailPage(page),
          limit: THUMBNAIL_PAGE_SIZE,
          orderBy: "featured",
        })
      );
    }
  }, [page]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // to load more images asynchronously depending on how many images they have viewed
    // this is purposefully added in this useEffect because the page doesn't seem to update when inside
    // no dependency array useEffect
    container?.addEventListener("scroll", handleScroll);

    return () => {
      container?.removeEventListener("scroll", handleScroll);
    };
  }, [page, containerRef.current]);

  if (!(hasReviewCoverPhotoForStyle && momentsWithCount?.totalCount > 2)) {
    return;
  }

  return (
    <div className="relative mt-2" data-test-id="review-thumbnail">
      <div
        className="flex flex-row overflow-y-hidden overflow-x-auto hide-scrollbar"
        ref={containerRef}
        data-test-id="review-thumbnail-carousel">
        {momentsWithCount?.totalCount > 5 && !isMobileViewport ? (
          <button
            onClick={() => scrollOnClick("left")}
            className="absolute left-0 bottom-6"
            data-test-id="review-thumbnail-carousel__left_arrow">
            <RightArrow className="rotate-180" />
          </button>
        ) : (
          ""
        )}
        <ThumbnailImages
          setReviewModalOpen={props.setReviewModalOpen}
          setReviewModalIndex={props.setReviewModalIndex}
          reviewsThumbnailPhotos={reviewsThumbnailPhotos}
        />
        {momentsWithCount?.totalCount > 5 && !isMobileViewport ? (
          <button
            onClick={() => scrollOnClick("right")}
            className="absolute right-0 bottom-6"
            data-test-id="review-thumbnail-carousel__right_arrow">
            <RightArrow />
          </button>
        ) : (
          ""
        )}
      </div>
    </div>
  );
}

ReviewThumbNailCarousel.propTypes = {
  productId: PropTypes.string,
  setReviewModalOpen: PropTypes.func,
  setReviewModalIndex: PropTypes.func,
};
